import { en, type EnMessages } from "./en"
import { es } from "./es"

export type Locale = "en" | "es"
export type MessageTree = EnMessages

const locales: Record<Locale, MessageTree> = { en, es }

let currentLocale: Locale = "en"

type NestedKey<T, Prefix extends string = ""> = {
  [K in keyof T & string]: T[K] extends Record<string, unknown>
    ? NestedKey<T[K], `${Prefix}${K}.`>
    : `${Prefix}${K}`
}[keyof T & string]

export type MessageKey = NestedKey<EnMessages>

function lookup(tree: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((node, part) => {
    if (node && typeof node === "object" && part in (node as Record<string, unknown>)) {
      return (node as Record<string, unknown>)[part]
    }
    return undefined
  }, tree)
}

function applyVars(value: string, vars?: Record<string, string | number>): string {
  if (!vars) return value
  return value.replace(/\{(\w+)\}/g, (_, name: string) => (
    vars[name] == null ? `{${name}}` : String(vars[name])
  ))
}

export function t(
  key: MessageKey,
  varsOrLocale?: Record<string, string | number> | Locale,
  localeArg?: Locale,
): string {
  const vars = typeof varsOrLocale === "object" ? varsOrLocale : undefined
  const locale = (typeof varsOrLocale === "string" ? varsOrLocale : localeArg) ?? currentLocale
  const value = lookup(locales[locale] || locales.en, key)
  if (typeof value === "string") return applyVars(value, vars)
  const fallback = lookup(locales.en, key)
  return typeof fallback === "string" ? applyVars(fallback, vars) : key
}

export function getLocale(): Locale {
  return currentLocale
}

export function setLocale(locale: Locale) {
  currentLocale = locale
}

export { en, es }
